'use client'
import TitleJp from '@/components/Atoms/TitleJp'
import More from '@/components/Molecules/More'
import { useWindowSize } from '@/hooks/useWindowSize'
import Image from 'next/image'
import { Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import InterviewCard from '@/components/Molecules/InterviewCard'
import Cursor from '@/components/Atoms/Cursor'
import { useCursor } from '@/hooks/useCursor'
import 'swiper/css'
import 'swiper/css/pagination'
import { useSuspenseQuery } from '@apollo/experimental-nextjs-app-support/ssr'
import { gql } from '@apollo/client'
import { PostNewsProps } from '@/types'

export default function Interview({
  text,
  subText,
  moreHref,
  categoryName,
}: {
  text: string
  subText: string
  moreHref?: string
  categoryName: string
}) {
  const query = gql`
  query GetInformationByCategory {
    getInformationByCategorySlug(categorySlug: ["${categoryName}"], page: 1, limit: 3) {
      collection {
        id
        title
        imageFileName
        slug
        count
        createdAt
        updatedAt
        categories {
          id
          name
          slug
        }
        informationTags {
          id
          name
          slug
        }
        informationMeta {
          id
          name
          title
        }
        informationInformationMeta {
          informationMetumId
          value
        }
      }
      pageInfo{
        isFirstPage
        isLastPage
        totalPages
      }
    }
  }
`

  const { data } = useSuspenseQuery<{
    getInformationByCategorySlug: { collection: PostNewsProps[] }
  }>(query)

  const informations = data.getInformationByCategorySlug.collection

  const pagination = {
    el: '.project-pagination',
  }
  const breakpoints = {
    769: {
      spaceBetween: 30,
    },
    320: {
      spaceBetween: 20,
    },
  }
  const { width } = useWindowSize()
  // Cursor properties
  const { mouseProperties, setMouseProperties, mouseDisplay, setMouseDisplay } =
    useCursor()
  return (
    <div className="relative">
      <TitleJp text={text} subText={subText} />
      {width > 768 ? (
        <div className="mt-8 md:mt-10 hidden md:flex flex-wrap md:[&>*]:w-[calc((100%_-_64px)_/_3)] md:[&>*]:mr-8 md:[&>*:nth-child(3n+3)]:mr-0 relative">
          {informations.map((item, index) => (
            <div key={`${item.title}-${index}`}>
              <InterviewCard
                title={item.title}
                categories={item.categories}
                tags={item.informationTags}
                images={
                  process.env.NEXT_PUBLIC_AWS_S3_URL +
                  '/' +
                  item.id +
                  '/original_' +
                  item.imageFileName
                }
                href={'/interview/' + item.slug}
                onEnter={() => {
                  setMouseProperties({
                    text: ' CLICK',
                    color: '#ffffff',
                    background: '#242A32',
                  })
                  setMouseDisplay(true)
                }}
                onLeave={() => setMouseDisplay(false)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="md:hidden w-[calc(50%_+_94vw)] md:w-[calc(50%_+_60vw)] relative">
          <Image
            src="/images/pickup.svg"
            alt="Pick up"
            width={167}
            height={89}
            className="hidden md:block w-[90px] md:w-[162px] h-auto absolute -top-[30px] md:top-[2px] left-3 md:left-[calc(50%_-_172px)] z-30"
          />
          <Swiper
            slidesPerView={'auto'}
            pagination={pagination}
            modules={[Pagination, Autoplay]}
            breakpoints={breakpoints}
            grabCursor={true}
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            className="project !pt-8 md:!pt-14"
            touchEventsTarget="container"
            loop
          >
            {informations.map((item, index) => (
              <SwiperSlide className="!w-1/2" key={`${item.title}-${index}`}>
                <InterviewCard
                  title={item.title}
                  categories={item.categories}
                  tags={item.informationTags}
                  images={
                    process.env.NEXT_PUBLIC_AWS_S3_URL +
                    '/' +
                    item.id +
                    '/original_' +
                    item.imageFileName
                  }
                  href={'/interview/' + item.slug}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
      {width > 768 && (
        <Cursor
          textMouse={mouseProperties.text}
          textColor={mouseProperties.color}
          background={mouseProperties.background}
          display={mouseDisplay}
          mixblendMode="mix-blend-hard-light"
        />
      )}
      <div className="flex items-center justify-between mt-8 md:mt-0">
        <div className="md:text-left md:absolute right-0 top-0 flex-1">
          <More
            href={`${moreHref ? moreHref : ''}`}
            text="View more"
            type="text"
          />
        </div>
        <div className="text-right dot-pagination project-pagination md:hidden md:mt-10 flex justify-end"></div>
      </div>
    </div>
  )
}
